import {BehaviorSubject} from 'rxjs';
import {mergeMap} from 'rxjs/operators';

import {type Action} from 'web-app/util/redux/action-types';
import {type RootState} from 'web-app/react/redux/root-state';
import {combineEpics} from 'web-app/util/redux-observable';
import famlyLiveEpics from 'web-app/react/famly-live/epics';
import groupActionsMenuEpics from 'web-app/react/institution-people/group-actions-menu/epics';
import parentalPermissionsMatrixEpics from 'web-app/react/parental-permissions/matrix/epics';
import paymentSetupEpics from 'web-app/react/feed/post/embeds/payment-setup/epics';
import famlyticsEpics from 'web-app/util/famlytics/epics';

import documents from './documents/epics';
import mainNavigation from './main-navigation/epics';
import publicFundingReport from './plans/public-funding-report/epics';
import debtReportV2 from './plans/debt-report-v2/epics';
import imageGallery from './image-gallery/epics';
import revenueReport from './plans/revenue-report/epics';
import batchInvoice from './plans/debt-report/batch-invoice/epics';
import invoiceSettings from './plans/invoice-settings/epics';
import employeeContacts from './profile/employee-profile/employee-contacts/epics';
import employeeKeyChildren from './profile/employee-profile/key-children/epics';
import volumeGroups from './plans/volume-settings/volume-groups/epics';
import managePlan from './plans/manage-plan/epics';
import plans from './plans/epics';
import changeRoleModal from './change-role-modal/epics';
import billingActivityOverview from './plans/debt-report/billing-activity-overview/epics';
import topBar from './top-bar/epics';
import session from './session/epics';
import billPayerForm from './components/bill-payer-form/epics';
import feedSwitcher from './feed/feed-switcher/epics';
import feed from './feed/epics';
import draftManager from './feed/drafts/epics';
import planSettings from './plans/plans-settings/epics';
import plansAndInvoices from './profile/child-profile/plans-and-invoices/epics';
import childProfileEpics from './profile/child-profile/epics';
import manageEmployeeRights from './manage-employee-rights/epics';
import newEmployeeEpics from './profile/new-employee/epics';
import billPayerProfileEpicsV2 from './profile/bill-payer-v2/epics';
import entityEpics from './entities/entity-epics';
import modalEpics from './modals-container/modal-epics';
import balances from './balances/epics';
import groupMoves from './institution/settings/group-moves/epic';
import manageGroups from './institution/settings/manage-groups/epics';
import newPost from './feed/post/new-post/epics';
import fileUpload from './components/file-upload/epics';
import typedFileUpload from './components/file-upload/typed-epics';
import mealRegistration from './meals/meal-registration/epics';
import organizationEpics from './organization/epics';
import famlyReportEpics from './famly-report/epics';
import accountSettingEpics from './account-settings/epics';
import notificationEpics from './notifications/epics';
import inboxEpics from './inbox/epics';
import asyncButtonEpics from './components/async-link-button/epics';
import institutionPeopleEpics from './institution-people/people/epics';
import leaveWidgetEpics from './profile/leave-widget/epics';
import {updateOnActionsEpic} from './components/higher-order/update-on-actions';
import contractEpics from './routes/contract/epics';
import activityEpics from './activities/epics';
import manageChildrenEpics from './manage-children/epics';
import inviteParentsEpics from './invite-parents/epics';
import abstractBugdgetEpics from './abstract-bugdet/epics';
import loginEpics from './login/epics';
import permissionEpics from './manage-employees/permissions/epics';
import newTagEpics from './institution/new-tag/epics';
import signInOut from './settings/content/app-settings/sign-in-out/epics';
import confirmEmailEpics from './routes/confirm-email/epics';
import twoFactorSettingsEpics from './account-settings/two-factor-settings/epics';
import homepageEpics from './homepage/epics';
import peoplePickerEpics from './people-picker/epics';
import institutionOverviewEpics from './institution/overview/epics';
import groupTagChartEpics from './institution/group-tag-chart/epics';
import autoLogoutEpics from './session/auto-logout/epics';
import listsEpics from './lists/epics';
import surveyEpics from './routes/account/survey/epics';
import financePowerPageEpics from './routes/account/institution/finance/epics';
import {entities} from './setup-entities';

export const epic$ = new BehaviorSubject(
    combineEpics<Action<any>, Action<any>, RootState>(
        activityEpics,
        contractEpics,
        updateOnActionsEpic,
        inboxEpics,
        notificationEpics,
        documents,
        mainNavigation,
        publicFundingReport,
        batchInvoice,
        invoiceSettings,
        revenueReport,
        imageGallery,
        debtReportV2,
        balances,
        volumeGroups,
        managePlan,
        plans,
        changeRoleModal,
        employeeContacts,
        employeeKeyChildren,
        billingActivityOverview,
        topBar,
        session,
        billPayerForm,
        feedSwitcher,
        feed,
        draftManager,
        planSettings,
        plansAndInvoices,
        childProfileEpics,
        manageEmployeeRights,
        newEmployeeEpics,
        billPayerProfileEpicsV2,
        entityEpics,
        modalEpics,
        groupMoves,
        manageGroups,
        newPost,
        fileUpload,
        mealRegistration,
        organizationEpics,
        famlyReportEpics,
        accountSettingEpics,
        asyncButtonEpics,
        institutionPeopleEpics,
        manageChildrenEpics,
        inviteParentsEpics,
        loginEpics,
        abstractBugdgetEpics,
        leaveWidgetEpics,
        permissionEpics,
        twoFactorSettingsEpics,
        homepageEpics,
        peoplePickerEpics,
        institutionOverviewEpics,
        newTagEpics,
        signInOut,
        confirmEmailEpics,
        paymentSetupEpics,
        groupTagChartEpics,
        listsEpics,
        typedFileUpload,
        famlyLiveEpics,
        groupActionsMenuEpics,
        parentalPermissionsMatrixEpics,
        famlyticsEpics,
        autoLogoutEpics,
        surveyEpics,
        financePowerPageEpics,
        ...entities.map(e => e.epics),
    ),
);

export default (action$, state$) => epic$.pipe(mergeMap(epic => epic(action$, state$, undefined)));
